/**
 * Icon — thin wrapper around `lucide-react`.
 *
 * The design system uses a string-based icon name (`name="calendar"`)
 * for portability. We map those names to the corresponding
 * `lucide-react` component and render it. Stroke and color follow
 * `currentColor` so icons pick up text color from their parent.
 */
import {
  Activity,
  ArrowLeft,
  ArrowRight,
  Bell,
  Calendar,
  Check,
  Clock,
  Compass,
  Eye,
  EyeOff,
  Flame,
  Globe,
  Heart,
  type LucideProps,
  MapPin,
  Menu,
  RefreshCw,
  Settings,
  Share,
  Trash2,
  TrendingUp,
  User,
  Users,
  X,
} from "lucide-react";
import type { CSSProperties } from "react";

/**
 * The set of icon names the design system actually uses. Adding a
 * glyph means adding it here, mapping it to a `lucide-react`
 * component, and (if you want type safety) extending
 * `IconName` below.
 */
const ICONS = {
  activity: Activity,
  "arrow-left": ArrowLeft,
  "arrow-right": ArrowRight,
  bell: Bell,
  calendar: Calendar,
  check: Check,
  clock: Clock,
  compass: Compass,
  eye: Eye,
  "eye-off": EyeOff,
  flame: Flame,
  globe: Globe,
  heart: Heart,
  "map-pin": MapPin,
  menu: Menu,
  refresh: RefreshCw,
  settings: Settings,
  share: Share,
  "trending-up": TrendingUp,
  trash: Trash2,
  user: User,
  users: Users,
  x: X,
} as const;

export type IconName = keyof typeof ICONS;

export interface IconProps {
  name: IconName;
  size?: number;
  color?: string;
  strokeWidth?: number;
  style?: CSSProperties;
  "aria-label"?: string;
}

export function Icon({
  name,
  size = 20,
  color = "currentColor",
  strokeWidth = 2,
  style = {},
  ...rest
}: IconProps) {
  const Cmp = ICONS[name];
  if (!Cmp) return null;

  // `lucide-react` already passes through to the underlying SVG with
  // `stroke="currentColor"`. We override stroke + color explicitly so
  // consumers can pass an explicit color (the design system sometimes
  // does — e.g. for nav active state).
  const lucideProps: LucideProps = {
    size,
    color,
    strokeWidth,
    "aria-hidden": rest["aria-label"] ? undefined : true,
    "aria-label": rest["aria-label"],
    style: { display: "inline-flex", flex: "none", ...style },
  };

  return <Cmp {...lucideProps} />;
}
